import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';

interface TerminalAsideProps {
  /** Open on mobile (controlled by TerminalHeader hamburger). Desktop ignores this. */
  mobileOpen: boolean;
  /** Close handler used by backdrop click and after navigating to a new route. */
  onClose: () => void;
}

interface MenuEntry {
  label: string;
  path: string;
}

interface MenuSection {
  /** Empty string = no section label (used for the top "dashboard" entry). */
  label: string;
  items: MenuEntry[];
}

/**
 * Menu content mirrors UserAsideMenu but in a text-first shape suited to
 * the terminal aesthetic. Kept deliberately duplicated (rather than shared)
 * so the classic menu and the terminal menu can evolve independently.
 */
const MENU_SECTIONS: MenuSection[] = [
  { label: '', items: [{ label: 'dashboard', path: '/dashboard' }] },
  {
    label: 'marketing',
    items: [
      { label: 'analytics', path: '/analytics' },
      { label: 'google ads', path: '/marketing/google-ads' },
      { label: 'mass email', path: '/marketing/mass-email' },
      { label: 'website submissions', path: '/website-submissions' },
    ],
  },
  {
    label: 'crm',
    items: [{ label: 'contacts', path: '/contacts' }, { label: 'monthly support', path: '/monthly-support' }],
  },
  {
    label: 'accounting',
    items: [
      { label: 'invoices & bills', path: '/accounting' },
      { label: 'recurring payments', path: '/recurring-payments' },
      { label: 'cash flow', path: '/accounting/cashflow' },
      { label: 'bank reconciliation', path: '/banking' },
    ],
  },
  {
    label: 'account',
    items: [{ label: 'settings', path: '/user/account/settings' }],
  },
];

const TerminalAside: React.FC<TerminalAsideProps> = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await signOut(getAuth());
      localStorage.clear();
      navigate('/login');
    } catch (err: any) {
      console.error('[TerminalAside] Sign out failed:', err?.message);
    }
  };

  // A menu path "owns" the route only when the route equals it or sits beneath
  // it on a SEGMENT boundary — so /accounting doesn't swallow /accounting/cashflow.
  // When several paths match (nested routes), the longest/most-specific one wins,
  // so exactly one item ever highlights.
  const { pathname } = location;
  const matches = (path: string): boolean =>
    pathname === path || pathname.startsWith(path + '/');
  const activePath = MENU_SECTIONS
    .flatMap((s) => s.items.map((i) => i.path))
    .filter(matches)
    .sort((a, b) => b.length - a.length)[0] || '';
  const isActive = (path: string): boolean => path === activePath;

  return (
    <>
      {mobileOpen && (
        <div
          className="term-aside__backdrop"
          onClick={onClose}
          role="button"
          aria-label="Close menu"
          tabIndex={-1}
        />
      )}
      <aside className={`term-aside ${mobileOpen ? 'term-aside--mobile-open' : ''}`}>
        <div className="term-aside__brand">
          <span className="term-aside__brand-sigil">$</span>
          <span className="term-aside__brand-name">web_construct</span>
        </div>

        <nav className="term-aside__nav">
          {MENU_SECTIONS.map((section, idx) => (
            <div key={idx} className="term-aside__section">
              {section.label && (
                <div className="term-aside__section-label">
                  ── {section.label} ──
                </div>
              )}
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`term-aside__item ${active ? 'term-aside__item--active' : ''}`}
                    onClick={onClose}
                  >
                    <span className="term-aside__glyph" aria-hidden="true">
                      {active ? '>' : ' '}
                    </span>
                    <span className="term-aside__label">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="term-aside__footer">
          <button type="button" className="term-aside__signout" onClick={handleSignOut}>
            <span className="term-aside__glyph" aria-hidden="true"> </span>
            <span className="term-aside__label">exit</span>
          </button>
          <div className="term-aside__hint">
            <span className="term-aside__kbd">⌘K</span> search &nbsp;<span className="term-aside__dim">(soon)</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default TerminalAside;
